import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Box,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TablePagination,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Partner } from '../../types/partner';
import { StorageAvatar } from '../common/StorageAvatar';
import { type Column, StandardTable } from '../common/UI';

export interface PartnerWithRelationship extends Partner {
  relationshipStatus?: 'approved' | 'rejected' | null;
}

interface PartnersTableProps {
  partners: PartnerWithRelationship[];
  currentClientId?: string | null;
  isLoading: boolean;
}

const PartnersTable: React.FC<PartnersTableProps> = ({ partners, currentClientId, isLoading }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'approved' | 'rejected'>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const filteredPartners = partners.filter((partner) => {
    const matchesSearch =
      (partner.companyName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (partner.capabilitiesSummary || '').toLowerCase().includes(searchTerm.toLowerCase());

    const isRejected = partner.relationshipStatus === 'rejected';

    let matchesStatus = true;
    if (statusFilter === 'approved') {
      matchesStatus = partner.relationshipStatus === 'approved';
    } else if (statusFilter === 'rejected') {
      matchesStatus = isRejected;
    }

    return matchesSearch && matchesStatus;
  });

  const paginatedPartners = filteredPartners.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const columns: Column<PartnerWithRelationship>[] = [
    {
      id: 'partnerInfo',
      label: 'Partner Information',
      render: (partner) => {
        const isRejected = partner.relationshipStatus === 'rejected';
        return (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <StorageAvatar
              src={partner.logoUrl}
              variant="square"
              imgProps={{ sx: { objectFit: 'contain' } }}
              sx={{
                width: 64,
                height: 64,
                bgcolor: partner.logoUrl
                  ? 'transparent'
                  : isRejected
                    ? theme.palette.text.disabled
                    : theme.palette.primary.main,
                fontSize: '1.5rem',
              }}
            >
              {(partner.companyName || 'U').charAt(0).toUpperCase()}
            </StorageAvatar>
            <Box sx={{ flexGrow: 1, ml: 1 }}>
              <Typography
                variant="h5"
                color={isRejected ? 'text.secondary' : 'text.primary'}
                fontWeight="bold"
                sx={{ mb: 0.5 }}
              >
                {partner.companyName || 'Unknown Partner'}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: 1.6,
                }}
              >
                {partner.capabilitiesSummary || 'No capabilities summary provided.'}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      id: 'status',
      label: 'Status',
      align: 'right',
      width: 160,
      render: (partner) => {
        const isOwnProfile = partner.id === currentClientId || partner.clientId === currentClientId;
        if (isOwnProfile) {
          return (
            <Chip
              icon={<StarIcon sx={{ color: 'white !important' }} />}
              label="Your Profile"
              size="medium"
              sx={{
                backgroundColor: 'black',
                color: 'white',
                fontWeight: 'bold',
                border: 'none',
              }}
            />
          );
        }
        return (
          <>
            {partner.relationshipStatus === 'approved' && (
              <Chip icon={<CheckCircleIcon />} label="Pre-approved" color="success" size="small" />
            )}
            {partner.relationshipStatus === 'rejected' && (
              <Chip
                icon={<VisibilityOffIcon />}
                label="Pre-rejected"
                color="default"
                size="small"
              />
            )}
          </>
        );
      },
    },
  ];

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search partners by company name or capabilities..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="status-filter-label">Filter by Status</InputLabel>
            <Select
              labelId="status-filter-label"
              id="status-filter-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as 'all' | 'approved' | 'rejected');
                setPage(0);
              }}
              label="Filter by Status"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {filteredPartners.length === 0 && !isLoading && (
        <Typography sx={{ my: 4, textAlign: 'center', color: 'text.secondary' }}>
          No partners match your search criteria.
        </Typography>
      )}

      {filteredPartners.length > 0 && (
        <StandardTable
          columns={columns}
          data={paginatedPartners}
          keyExtractor={(partner) => partner.id || ''}
          onRowClick={(partner) => navigate(`/partner-details/${partner.id}`)}
          rowSx={(partner) => {
            const isRejected = partner.relationshipStatus === 'rejected';
            return {
              opacity: isRejected ? 0.6 : 1,
              backgroundColor: isRejected ? theme.palette.action.hover : 'inherit',
              '& > td': { py: 3 }, // Make rows taller
            };
          }}
        />
      )}

      {filteredPartners.length > 0 && (
        <TablePagination
          component="div"
          count={filteredPartners.length}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      )}
    </Box>
  );
};

export default PartnersTable;
